export default {
  // The feature's tab strip. Only the two queues carry a figure, and the hint names the window: the
  // strip is on all six pages, so its period is nobody's choice and must not be mistaken for the
  // selection made on the page below it.
  nav: {
    badge_behind: "This many employees are missing at least one day this month, counted across the whole workforce.",
    badge_violations:
      "This many breaches of the working time act fall in the current month, counted across the whole workforce.",
  },
  // The one sentence for "still fetching", wherever an empty state would otherwise stand. An empty
  // state is a claim about data ("nobody is clocked in", "no violations found"), and while the
  // answer is still in flight every one of those claims is unfounded — and the bar above it is
  // already saying something else. Deliberately without a subject: the same sentence stands over
  // accounts, violations, the attendance board and the week grid.
  loading: "Loading …",
  my: {
    empty: "Nothing recorded for this week yet.",
    title: "My hours",
    subtitle: "Your week at a glance: start, end and breaks.",
    add: "Record time",
    clock_in: "Clock in",
    clock_out: "Clock out",
    previous_week: "Previous week",
    next_week: "Next week",
    this_week: "This week",
    still_running: "running",
    saved: "Time saved.",
    save_failed: "The entry was not saved. Your input is still in the form.",
    delete: "Delete",
    delete_title: "Delete this entry?",
    delete_text: "The entry of {date} will be deleted. This cannot be undone.",
    deleted: "Entry deleted.",
    discard: "Discard",
    discard_running_title: "Discard the running entry?",
    discard_running_text: "The entry running since {time} will be taken back. No time will be recorded.",
    discarded: "Entry discarded.",
    clocked_out: "Clocked out at {time} · {hours} h worked.",
    clock_in_discarded: "Clock-in and clock-out fell in the same second; nothing was recorded.",
    add_break_now: "Add break",
    stale_clock_out_title: "End the day of {date}?",
    stale_clock_out_text:
      "Clocking out ends the day of {date}; that makes it {duration} h in one stretch. The end time can be corrected afterwards.",
    stale_clocked_out: "The day of {date} was closed · {hours} h. Please correct the end time.",
    duplicate_to: "Duplicate as {date}",
    running_not_duplicable: "Still running; there is no end time to copy yet.",
    running_not_editable: "Still running; clock out first.",
    approved_not_editable: "Approved; it has to be reopened first.",
    week_actual: "Actual",
    week_target: "Target",
    week_balance: "Balance",
    week_days: "Days recorded",
    week_entries: "{count} entries",
    week_target_share: "{percent}% of target",
    week_no_model: "No working-time model",
    week_chart: "The week, day by day",
    week_projects: "Projects this week",
    week_projects_more: "{count} more · {hours} h",
    entries_title: "Recorded hours",
    filter_by_day: "Show only {day}",
    filter_by_project: "Show only {project}",
    filter_clear: "Clear filter",
    filter_empty: "No entry matches this selection.",
    filter_count: "{shown} of {total}",
    loading: "Loading hours …",
  },
  clock: {
    idle_title: "Nothing running",
    idle_hint: "Recording starts at {time}.",
    running_since: "since {time}",
    running_since_date: "since {date}, {time}",
    stale_running:
      "You have a day still open from {date}. Clocking out ends that day. Check the hours afterwards and correct them if you forgot to clock out.",
    unreachable_running:
      "The server reports a running entry this page cannot show: the open day is too far back. It is listed on the attendance board; ask whoever looks after time tracking to close it.",
    no_project: "No project",
    no_project_running: "No project assigned",
    elapsed: "Worked so far, recorded breaks deducted",
    break_start: "Break",
    break_end: "End break",
    on_break_since: "On a break since {time} · {duration}",
    break_recorded: "{duration} break recorded",
    chip_on_break: "On a break since {time}; the worked figure is holding. Go to My hours",
    chip_open: "Running since {time}. Open My hours",
    chip_open_date: "Running since {date}, {time}. Open My hours",
    chip_since_date: "since {date}",
    chip_to_my_times: "Open My hours",
  },
  review: {
    title: "Review hours",
    subtitle: "Check, correct and approve recorded hours for payroll.",
    only_gaps: "Only with missing days",
    no_selection: "Select employees first.",
    select_employees: "Employees",
    select_all_employees: "Select all",
    clear_all_employees: "Clear selection",
    employees_count: "{selected} of {total} employees selected",
    employees_count_selected: "{selected} selected",
    select_all_shown: "Select all {count} shown",
    remembered: "Selection from your last visit",
    filter_status: "Status",
    filter_project: "Project",
    // The line under each name in the employee menu: how many of the loaded entries are that
    // person's. Only for the selected employees — nothing was loaded for anybody else.
    approve: "Approve",
    reject: "Reject",
    reopen: "Reopen",
    crew_action: "Group entry",
    absence_action: "Absence",
    absence_entry: "Enter an absence for one or more employees",
    absence_saved: "Absence entered. | {count} absences entered.",
    absence_skipped: "Skipped: {days}.",
    absence_replaced: "Replaced: {days}.",
    absence_removed: "Absence removed.",
    absence_remove_title: "Remove absence?",
    absence_remove_text:
      "{absence} for {name} on {date} will be removed, in personnel planning as well. The rest from {from} to {to} stays.",
    absence_remove_text_single: "{absence} for {name} on {date} will be removed, in personnel planning as well.",
    create_here: "Record time here",
    crew_entry_one: "Record time",
    crew_submit_one: "Record",
    crew_entry: "Record time for several employees",
    crew_submit: "Record for all",
    column_source: "Recorded via",
    break_undocumented: "{duration} break not documented",
    break_undocumented_hint:
      "No break times are recorded for this day. The statutory minimum was deducted; the record " +
      "cannot say when the break was taken (§ 16 Abs. 2 ArbZG).",
    column_status: "Status",
    total: "Total",
    approve_running_blocked: "A day that is still running cannot be approved.",
    // Month-end is exactly this one action: approve everything still open in the period for the
    // selected people. Two roles, two keys — as with the group entry: the button sits next to
    // "Export" and has to be short, the dialog title may spell the question out, and does.
    approve_period: "Approve period",
    approve_period_hint:
      "Approve every open entry of the selected employees in this period, including the ones the filter is currently hiding.",
    // Not "nothing is waiting" while nobody is selected: that is what `no_selection` is for.
    approve_period_none: "Nothing in the selected period is waiting for approval.",
    approve_period_title: "Approve everything in this period?",
    approve_period_text:
      "{count} entries of {people} employees in the period {from} – {to} will be approved, regardless of what the filters are currently showing.",
    approve_period_skipped_running: "{count} still running entries stay open: a day without an end cannot be approved.",
    approve_period_skipped_approved:
      "{count} already approved entries stay untouched, so it remains traceable who approved them.",
    approve_period_skipped_own: "{count} entries you recorded yourself stay open: someone else approves them.",
    approve_period_only_own:
      "Only entries you recorded yourself are waiting in this period. Someone else approves them.",
    self_review_blocked: "Entries you recorded yourself are approved by someone else.",
    edit: "Edit entry",
    delete: "Delete entry",
    delete_title: "Delete this entry?",
    delete_text: "{name}'s entry of {date} will be deleted. This cannot be undone.",
    deleted: "Entry deleted.",
    saved: "Entry saved.",
    approved: "{count} entries approved.",
    rejected: "{count} entries rejected.",
    reopened: "{count} entries reopened.",
    loading: "Loading times …",
    reviewed_by: "Reviewed by {name} · {at}",
    reviewed_at: "Reviewed on {at}",
    recorded_by: "Recorded by {name}",
    captured_via: "Recorded via {channel} · {at}",
    clocked_out_via: "Clocked out via {channel} · {at}",
    capture_location: "Open the {kind} location on the map",
    capture_location_accuracy: "Open the {kind} location on the map · ±{accuracy} m",
    capture_kind_in: "recording",
    capture_kind_out: "clock-out",
    stamped_late: "Stamped {stamped}, received {received}",
    location_missing: {
      UNAVAILABLE: "No location found",
    },
    note_label: "Reason",
    note_optional: "Optional",
    note_required: "Please give a reason.",
    note_placeholder_reject: "What is wrong with this day?",
    note_title_approve: "Approve times",
    note_title_reject: "Reject times",
    note_title_reopen: "Reopen times",
    note_prompt_approve: "{count} entries will be approved for payroll. A note is optional.",
    note_prompt_reject:
      "{count} entries will be rejected. The employee sees the reason under My hours, on the rejected day itself; it disappears once they correct that day.",
    note_prompt_reopen: "{count} approved entries will be reopened. A note is optional.",
  },
  coverage: {
    missing_count: "{count} missing day | {count} missing days",
    complete: "Fully recorded",
    note: "Public holidays are not modelled. They show as missing days unless an absence was assigned.",
    partial_absence: "{absence} (half day)",
    state: {
      RECORDED: "Recorded",
      RUNNING: "Running",
      MISSING: "Missing",
      ABSENT: "Away",
      OFF: "Not a working day",
      NO_MODEL: "No working-time model",
      FUTURE: "Still to come",
    },
  },
  form: {
    date: "Date",
    period_from: "From",
    period_to: "To",
    from_time: "Start",
    to_time: "End",
    breaks: "Breaks",
    add_break: "Add break",
    project: "Project",
    shift: "Shift",
    note: "Note",
    employees: "Employees",
    month: "Month",
    // Who typed the day in — not necessarily whose day it is: a Polier records for his crew.
    // Appears in the activity log only; the form does not ask for it.
    recorded_by: "Recorded by",
    capture_channel: "Recorded via",
    captured_at: "Recorded at",
    clock_out_channel: "Clocked out via",
    clocked_out_at: "Clocked out at",
    stamped_at: "Stamped at (phone)",
    location_missing: "Location",
    clock_out_stamped_at: "Clocked out at (phone)",
    clock_out_location_missing: "Location at clock-out",
    reviewed_at: "Reviewed at",
    reviewed_by: "Reviewed by",
    suggested_from_planning: "From personnel planning",
    suggested_from_membership: "Only assigned project",
    suggested_from_membership_crew: "The selected employees' only project",
    suggested_mixed: "The selected employees are planned differently on this day. Please pick a project.",
    suggested_too_many: "Too many employees selected for a project suggestion. Please pick a project.",
    crew_figures_shared: "Calculated with “{model}”, the working-time model of everybody selected.",
    crew_figures_mixed:
      "The selected employees are on different working-time models. Below is the recorded time only; the server works out each person's break deduction and target individually.",
    crew_figures_none:
      "None of the selected employees has a working-time model on this day, so there is no target and no statutory break deduction.",
    crew_figures_not_asked:
      "Above {max} selected employees the working-time model is not checked. Below is the recorded time only; the server works out each person's break deduction and target individually.",
    crew_figures_unknown:
      "The selected employees' working-time model is not known. Below is the recorded time only; the server works out each person's break deduction and target individually.",
    project_optional: "Project (optional)",
    shift_optional: "Shift (optional)",
    shift_hint: "Fills in the shift's hours.",
    title_create: "Record time",
    title_edit: "Edit entry",
    submit_create: "Record",
    minutes: "{minutes} min",
    remove_break: "Remove break",
    no_breaks: "No break recorded. The statutory break is deducted regardless.",
    end_after_start: "Start and end are identical, so that is no working time.",
  },
  figures: {
    gross: "On site",
    break: "Break",
    worked: "Worked",
    target: "Target",
    balance: "Balance",
    break_undocumented: "No break times recorded. The statutory minimum was deducted anyway; enter when the break was.",
    statutory_break_absorbed:
      "The recorded break is shorter than the statutory one. The longer of the two is deducted.",
    break_recorded: "{recorded} recorded",
    no_model: "No working-time model assigned, so there is no target.",
    exceeds_daily_maximum:
      "Over the {max} daily maximum the working-time model allows. The server will not take the day as it stands.",
    hours_unit: "h",
  },
  // ---------------------------------------------------------------------------------------------
  team: {
    attendance_title: "Currently clocked in",
    on_the_clock: "{count} clocked in",
    nobody_clocked_in: "Nobody is clocked in right now.",
    unknown_employee: "Unknown employee",
    since: "since {time}, {date}",
    on_break_since: "on a break since {time}",
    attendance_hint: "On the clock, breaks included",
    today: "today",
    forgotten_clock_out: "Forgot to clock out?",
    stale_count: "{count} not clocked out",
    refreshing: "Refreshing …",
    updated_at: "As of {time}",
    refresh: "Refresh",
  },

  account: {
    credit: "Credited",
    title: "Working time account",
    closing_at: "Standing on {date}",
    carry_over: "Carry-over",
    closing: "Balance",
    print_failed: "The monthly statement could not be created.",
    export_hint: "Select at least one employee. The export covers exactly what the filters describe.",
    no_opening: "No opening balance",
    export: "Time account as CSV",
    export_hint_short: "Carry-over, balance and standing per employee",
    your_balance: "Your working time account:",
    as_of: "as of {date}",
    months: "Month by month",
    no_months: "No month has been recorded this year yet.",

    opening_title: "Working time account opening balance",
    opening_hint: "Overtime on the reference date, e.g. from the old system. Earlier days are not counted again.",
    opening_balance: "Opening balance",
    opening_date: "Reference date",
    opening_line: "Opening balance {balance} h on {date}",
    opening_add: "Enter opening balance",
  },

  compliance: {
    title: "Working time violations",
    violations: "Violations",
    warnings: "Warnings",
    rule: {
      DAILY_MAXIMUM_EXCEEDED: "Daily maximum",
      REST_PERIOD_TOO_SHORT: "Rest period",
      BREAK_TOO_SHORT: "Break too short",
      BREAK_NOT_DOCUMENTED: "Break not documented",
      WEEKLY_MAXIMUM_EXCEEDED: "Weekly hours",
      OPEN_ENTRY_STALE: "Still running",
    },
  },

  matrix: {
    mode: {
      worked: "Hours",
      balance: "Balance",
      status: "Status",
      violations: "Violations",
    },
    account_hint: "Carry-over and standing ignore the status and project filters.",
    trail: "Record",
    verdict_scope: "Affects {count} entries",
    no_gaps: "No missing days in this period.",
    no_gaps_hint: "Every expected day is recorded for all {count} selected employees.",
    show_all: "Show all employees",
    picked_cells: "{count} cell selected | {count} cells selected",
    picked_entries: "{count} entry affected | {count} entries affected",
    picked_clear: "Clear selection",
    picked_absence: "Enter absence",
    shown: "{shown} of {loaded} entries shown",
    legend: {
      worked: "Cell: hours recorded · • missing · abbreviation away · ▸ running",
      balance: "Cell: balance against target · green over, amber under",
      status: "Cell: S submitted · A approved · R rejected · O open",
      violations: "Cell: § breach of the working time act",
    },
    status_short: {
      OPEN: "O",
      SUBMITTED: "S",
      APPROVED: "A",
      REJECTED: "R",
    },
    empty: {
      MISSING: "A working day with nothing recorded",
      ABSENT: "Away",
      OFF: "Not a working day",
      NO_MODEL: "No working-time model",
      FUTURE: "Still in the future",
      RECORDED: "Recorded",
      RUNNING: "Still running",
    },
    day_actions: "Judge this day",
    employee_actions: "Account and approval",
    absence_partial: "{name}, half day",
    scope_day: "Everyone on {date}",
    nothing_to_approve: "Nothing here is waiting for a decision.",
  },

  absence: {
    title: "Enter absence",
    kind: "Kind of absence",
    from: "From",
    to: "To",
    span: {
      FULLDAY: "Full day",
      HALFDAY: "Half day",
      CUSTOM: "From to",
    },
    tab_time: "Time",
    tab_absence: "Absence",
    unpaid: "unpaid",
    conflicts_title:
      "Does not fit beside the existing absence on one day | Does not fit beside the existing absences on {count} days",
    conflicts_more: "and {count} more",
    conflict_skip: "Leave these days out, enter the rest",
    conflict_replace: "Replace the existing absence on these days",
    remove_day: "Remove day",
    credit: "{hours} credited to the time account",
    submit: "Enter",
    end_after_start: "The end is before the start.",
    hint: "Also shown in personnel planning. Paid absences are credited to the time account. Two half days fit on one day, a full day stands alone.",
    range: "{from} to {to}",
    days: "{count} day | {count} days",
    create_time: "Record time",
  },

  export: {
    certificate: "Monthly statement (PDF)",
    title: "Export",
    csv_german: "CSV (Excel, German)",
    csv_german_hint: "Semicolon, decimal comma",
    csv_technical: "CSV (technical)",
    own_week: "Week on screen",
    failed: "The export could not be created.",
  },

  project: {
    empty: "No time was booked to this project in the selected period.",
    title: "Recorded time",
    days: "Recorded days",
    people: "Employees",
    by_phase: "By phase",
    no_phase: "No phase",
  },
  reconciliation: {
    open_day: "Open day",
    empty: "All covered: every clocked day appears in [[hwr:a/an]] [[hwr]] or [[cr:a/an]] [[cr]].",
    title: "Check against reports",
    loading: "Loading check …",
    filter_all: "All",
    kind: {
      MISSING_REPORT: "Report missing",
      DEVIATION: "Deviation",
    },
    missing_days: "Days without a report",
    missing_hours: "Hours without a report",
    deviations: "Deviations",
    column_tracked: "Clocked",
    column_reported: "Reported",
    column_difference: "Difference",
    column_kind: "Finding",
    open_hourly_wage_report: "To the [[hwr]]",
    open_construction_report: "To the [[cr]]",
    tolerance_note: "A difference of more than {tolerance} counts as a deviation. For [[cr.pl]] the full shift counts.",
  },

  status: {
    OPEN: "Running",
    SUBMITTED: "Recorded",
    APPROVED: "Approved",
    REJECTED: "Rejected",
  },
  source: {
    SELF: "Self-recorded",
    CREW: "Group entry",
    IMPORT: "Imported",
  },
  capture_channel: {
    WEB: "Web",
    MOBILE: "Mobile app",
    UNKNOWN: "Unknown",
  },
  model: {
    empty: "No working-time model defined yet.",
    title_one: "Working time model",
    title: "Working-time models",
    title_create: "Add working-time model",
    title_edit: "Edit working-time model",
    submit_create: "Create",
    subtitle: "Full-time, part-time and shift as the basis for target hours and break deduction.",
    add: "Add model",
    edit: "Edit model",
    remove: "Remove model",
    remove_title: "Remove working-time model?",
    remove_text:
      "“{name}” can no longer be assigned. If time was recorded under it or somebody is assigned to it, everything stays as it is and it moves to the archive, where you can restore it at any time. Otherwise it is deleted.",
    restore: "Restore model",
    archived_section: "Archived ({count})",
    name: "Name",
    type: "Type",
    color: "Colour",
    pick_color: "Pick colour",
    weekly_target_hours: "Weekly hours",
    weekly_hours_short: "h/week",
    max_daily_hours: "Maximum hours per day",
    rounding_minutes: "Rounding (minutes)",
    rounding_hint: "Leave empty for no rounding.",
    limits: "Limits",
    day_column: "Day",
    days_hint: "No target means: not a working day.",
    day_off: "off",
    zero_hint:
      "A day set to 0 expects no hours, so anything recorded there is entirely overtime. Empty means: not a working day.",
    week_sum_match: "The week adds up to {hours} h and matches the weekly hours.",
    week_sum_diff: "The week adds up to {hours} h; the weekly hours say {target}.",
    days: "Weekly pattern",
    target_hours: "Target",
    planned_from: "Start",
    planned_to: "End",
    break_rules: "Break rules",
    break_rules_hint: "From {hours} h, at least {minutes} min break (§ 4 ArbZG).",
    add_break_rule: "Add rule",
    remove_break_rule: "Remove rule",
    after_hours: "From hours worked",
    min_minutes: "Minimum break (min)",
    shift_templates: "Shifts",
    shift_templates_hint: "Shift models only. The night shift may run past midnight.",
    add_shift: "Add shift",
    remove_shift: "Remove shift",
    default_label: "Default for new employees",
    default_replaces: "Replaces “{name}” as the default.",
    default_chip: "Default",
    unassigned: "{count} employee without a model | {count} employees without a model",
    unassigned_hint: "Without a model a day has no target and never counts as missing. Open to assign:",
    assignment: {
      title: "Working-time model",
      model: "Model",
      valid_from: "Valid from",
      valid_to: "Valid to",
      valid_to_optional: "Valid to (optional)",
      open_ended: "Ongoing",
      add: "Add change",
      assign: "Assign model",
      prefilled: "Prefilled with the default model. “Valid from” follows the entry date.",
      edit: "Edit assignment",
      closes_previous: "“{name}” ends on {date} as a result.",
      none: "No model assigned",
      current: "Current",
      remove: "Remove assignment",
      remove_title: "Remove assignment?",
      remove_text:
        "The assignment will be removed. Hours already recorded keep their target; days recorded in that period from now on will have none.",
    },
  },
  weekday: {
    MONDAY: "Monday",
    TUESDAY: "Tuesday",
    WEDNESDAY: "Wednesday",
    THURSDAY: "Thursday",
    FRIDAY: "Friday",
    SATURDAY: "Saturday",
    SUNDAY: "Sunday",
  },
  type: {
    FULL_TIME: "Full-time",
    PART_TIME: "Part-time",
    SHIFT: "Shift work",
  },
}
